import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Filter, Loader2, Play, Sparkles } from 'lucide-react'
import type { TestRun } from '@sophia/shared-schemas'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { testSetsApi, testSetsQueryKey } from '../api'
import { OverviewTab } from '../components/OverviewTab'
import { RulesTab } from '../components/RulesTab'
import { ConfusionTab } from '../components/ConfusionTab'
import { PredictionsTab } from '../components/PredictionsTab'
import { InsightsTab } from '../components/InsightsTab'
import { ComparisonTab } from '../components/ComparisonTab'
import { extractApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

type Tab = 'overview' | 'rules' | 'confusion' | 'predictions' | 'comparison' | 'insights'

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'rules', label: 'Regras' },
  { id: 'confusion', label: 'Confusão' },
  { id: 'predictions', label: 'Predições' },
  { id: 'comparison', label: 'Comparação' },
  { id: 'insights', label: 'Insights' },
]

export function TestSetDetailPage() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>('overview')
  const [error, setError] = useState<string | null>(null)

  const classFilter = searchParams.get('class')

  const { data, isLoading, isError, error: queryError } = useQuery({
    queryKey: testSetsQueryKey.detail(id!),
    queryFn: () => testSetsApi.detail(id!),
    enabled: !!id,
  })

  const runMut = useMutation({
    mutationFn: () => testSetsApi.run(id!),
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: testSetsQueryKey.detail(id!) })
      qc.invalidateQueries({ queryKey: testSetsQueryKey.list() })
    },
    onError: (err) => setError(extractApiError(err).message),
  })

  const runs = data?.runs ?? []
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const currentRun = useMemo<TestRun | null>(() => {
    if (!runs.length) return null
    if (selectedRunId) return runs.find((r) => r.id === selectedRunId) ?? runs[0]
    return runs[0]
  }, [runs, selectedRunId])

  // run anterior concluída (runs vêm ordenadas desc por startedAt)
  const previousRun = useMemo<TestRun | null>(() => {
    if (!currentRun) return null
    const idx = runs.findIndex((r) => r.id === currentRun.id)
    for (let i = idx + 1; i < runs.length; i++) {
      if (runs[i].status === 'COMPLETED' && runs[i].metrics) return runs[i]
    }
    return null
  }, [runs, currentRun])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fg-3">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/benchmarks" className="inline-flex items-center gap-1 text-sm text-fg-3 hover:text-fg-1">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <p className="text-sm text-danger">Erro ao carregar: {extractApiError(queryError).message}</p>
      </div>
    )
  }

  const { testSet } = data

  function pickClass(classId: string | null) {
    const next = new URLSearchParams(searchParams)
    if (classId) next.set('class', classId)
    else next.delete('class')
    setSearchParams(next, { replace: true })
  }

  const classFilterName =
    classFilter && currentRun?.metrics
      ? currentRun.metrics.perClass.find((c) => c.classId === classFilter)?.className ?? null
      : null

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/benchmarks" className="inline-flex items-center gap-1 text-sm text-fg-3 hover:text-fg-1 mb-3">
          <ArrowLeft className="h-4 w-4" /> Bases de teste
        </Link>
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="font-display text-2xl font-bold tracking-tight text-fg-1 truncate">{testSet.name}</h1>
              {currentRun?.status === 'COMPLETED' && <Badge tone="success">Concluída</Badge>}
              {currentRun?.status === 'RUNNING' && <Badge tone="warning">Rodando</Badge>}
              {currentRun?.status === 'FAILED' && <Badge tone="danger">Falhou</Badge>}
            </div>
            <div className="text-xs text-fg-3 mt-1.5 flex items-center gap-2 flex-wrap">
              <span>
                <span className="data-mono text-fg-2">{testSet.totalRows}</span> linhas
              </span>
              <span className="text-border-strong">·</span>
              <span>
                <span className="data-mono text-fg-2">{testSet.classLabels.length}</span> classes na planilha
              </span>
              <span className="text-border-strong">·</span>
              <span>
                <span className="data-mono text-fg-2">{runs.length}</span> runs
              </span>
              {currentRun && (
                <>
                  <span className="text-border-strong">·</span>
                  <span>última execução {new Date(currentRun.startedAt).toLocaleString('pt-BR')}</span>
                </>
              )}
              {testSet.sourceFilename && (
                <span className="data-mono text-fg-3/70 truncate">{testSet.sourceFilename}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {runs.length > 1 && (
              <select
                value={currentRun?.id ?? ''}
                onChange={(e) => setSelectedRunId(e.target.value)}
                className="h-10 px-3 rounded-md border border-border bg-bg-surface text-sm text-fg-1"
              >
                {runs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {new Date(r.startedAt).toLocaleString('pt-BR')} — {r.status}
                  </option>
                ))}
              </select>
            )}
            <Button onClick={() => runMut.mutate()} loading={runMut.isPending}>
              <Play className="h-4 w-4" />
              {runs.length === 0 ? 'Rodar com regras atuais' : 'Rodar novamente'}
            </Button>
          </div>
        </header>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">{error}</div>
      )}

      {runMut.isPending && (
        <div className="text-sm text-fg-3 flex items-center gap-2 px-4 py-3 rounded-md border border-border-subtle bg-bg-subtle">
          <Loader2 className="h-4 w-4 animate-spin" />
          Rodando cascata contra as regras atuais + gerando insights LLM…
        </div>
      )}

      {!currentRun ? (
        <div className="rounded-md border border-dashed border-border bg-bg-subtle/40 p-10 text-center">
          <Sparkles className="h-6 w-6 text-fg-3 mx-auto mb-3" />
          <h3 className="font-display font-bold text-base text-fg-1 mb-1">Nenhuma run ainda</h3>
          <p className="text-sm text-fg-3 max-w-md mx-auto">
            Clique em "Rodar com regras atuais" pra processar a base com todas as regras ativas. Vai gerar métricas
            globais, por classe, por regra e insights do LLM.
          </p>
        </div>
      ) : (
        <>
          {classFilterName && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-brand-50 border border-brand-100 text-sm">
              <Filter className="h-3.5 w-3.5 text-brand-700" />
              <span className="text-fg-2">
                Filtrando por classe: <span className="font-semibold text-brand-700">{classFilterName}</span>
              </span>
              <button
                type="button"
                onClick={() => pickClass(null)}
                className="text-xs text-brand-700 hover:text-brand-800 underline ml-auto"
              >
                limpar
              </button>
            </div>
          )}

          <nav className="flex border-b border-border-subtle gap-1 -mt-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  'px-4 py-2.5 text-sm font-medium relative transition-colors rounded-t-md',
                  tab === t.id ? 'text-brand-700' : 'text-fg-3 hover:text-fg-1 hover:bg-bg-subtle/60',
                )}
              >
                {t.label}
                {tab === t.id && (
                  <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-brand-600 rounded-pill" />
                )}
              </button>
            ))}
          </nav>

          {currentRun.status === 'FAILED' && (
            <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">
              Esta run falhou: {currentRun.errorMessage ?? 'erro desconhecido'}
            </div>
          )}

          {currentRun.status === 'RUNNING' && (
            <Badge tone="warning">Esta run ainda está rodando…</Badge>
          )}

          {tab === 'overview' && (
            <OverviewTab
              run={currentRun}
              previousRun={previousRun}
              classFilter={classFilter}
              onPickClass={pickClass}
            />
          )}
          {tab === 'rules' && <RulesTab run={currentRun} classFilter={classFilter} />}
          {tab === 'confusion' && <ConfusionTab run={currentRun} classFilter={classFilter} />}
          {tab === 'predictions' && (
            <PredictionsTab testSetId={testSet.id} run={currentRun} classFilter={classFilter} />
          )}
          {tab === 'comparison' && <ComparisonTab runs={runs} />}
          {tab === 'insights' && <InsightsTab run={currentRun} />}
        </>
      )}
    </div>
  )
}
